<script setup>
/**
 * The window every list and chart on the screen is looking through: two dates,
 * or neither, which means everything there is.
 *
 * It sits above the search because that is what it is — the first question
 * asked of the ledger, before any filter narrows what is inside it. The same
 * range can be swept straight off a chart; this is where you read it back, and
 * the way in without a mouse.
 *
 * The keyboard: R opens it (stores/shortcuts), and while it is open 1 and 2
 * go to the two dates, 3 onwards pick a range, Esc cancels and ⌘/Ctrl+Enter
 * applies. A number typed into a date field is part of the date. The focus
 * comes in when it opens, stays in while Tab goes round, and goes back to the
 * button when it closes. Open or closed is `state.ui.rangeOpen`, so the page's
 * own shortcuts know to keep out of its way.
 */
import { computed, nextTick, ref, watch, onBeforeUnmount } from 'vue'
import Icon from './ui/Icon.vue'
import Kbd from './ui/Kbd.vue'
import { chartRange, clearDateRange, setDateRange } from '../stores'
import { state } from '../stores/state'
import {
  addDays,
  addMonths,
  endOfMonth,
  monthOf,
  shortDay,
  startOfMonth,
  thisMonth,
  today as todayISO,
} from '../lib/dates'
import { ariaCombo, isTyping, keysOf, matches } from '../lib/shortcuts'
import { t } from '../stores/i18n'

const open = computed({
  get: () => state.ui.rangeOpen,
  set: (value) => (state.ui.rangeOpen = value),
})
const draft = ref({ from: '', to: '' })
const trigger = ref(null)
const panel = ref(null)
const fromEl = ref(null)
const toEl = ref(null)

const label = computed(() => {
  const { from, to } = chartRange.value
  if (!from && !to) return t('range.allTime')
  if (from && to) return `${shortDay(from)} — ${shortDay(to)}`
  return from ? t('range.from', { day: shortDay(from) }) : t('range.upTo', { day: shortDay(to) })
})

const show = () => (open.value = true)
const toggle = () => (open.value = !open.value)

const apply = () => {
  setDateRange(draft.value.from || null, draft.value.to || null)
  open.value = false
}

const clear = () => {
  clearDateRange()
  open.value = false
}

/** The handful of spans worth a single tap. */
const PRESETS = [
  { key: 'thisMonth', of: () => [startOfMonth(thisMonth()), endOfMonth(thisMonth())] },
  { key: 'last7', of: () => [addDays(todayISO(), -6), todayISO()] },
  { key: 'last30', of: () => [addDays(todayISO(), -29), todayISO()] },
  { key: 'last3Months', of: () => [startOfMonth(monthOf(addMonths(todayISO(), -2))), todayISO()] },
  { key: 'thisYear', of: () => [`${todayISO().slice(0, 4)}-01-01`, todayISO()] },
]
const usePreset = (preset) => {
  setDateRange(...preset.of())
  open.value = false
}

/** Number keys, in the order the popover reads: the two dates, the ranges, then all time. */
const NUMBERED = [
  () => fromEl.value?.focus(),
  () => toEl.value?.focus(),
  ...PRESETS.map((preset) => () => usePreset(preset)),
  clear,
]
/** The number that picks a preset, for its key cap. */
const numberOf = (preset) => String(PRESETS.indexOf(preset) + 3)
const ALL_TIME = String(NUMBERED.length)

const FOCUSABLE = 'button:not([disabled]), input:not([disabled])'

function onKey(event) {
  if (event.key === 'Escape') {
    event.preventDefault()
    open.value = false
    return
  }
  if (matches(event, 'mod+enter')) {
    event.preventDefault()
    apply()
    return
  }
  if (event.key === 'Tab') {
    const list = [...(panel.value?.querySelectorAll(FOCUSABLE) ?? [])]
    if (!list.length) return
    const at = list.indexOf(document.activeElement)
    if (event.shiftKey && at <= 0) {
      event.preventDefault()
      list[list.length - 1].focus()
    } else if (!event.shiftKey && (at === -1 || at === list.length - 1)) {
      event.preventDefault()
      list[0].focus()
    }
    return
  }
  // A digit in a date field is part of the date; anywhere else in here, a choice.
  if (isTyping(event.target) || event.ctrlKey || event.metaKey || event.altKey) return
  const n = Number(event.key)
  if (Number.isInteger(n) && n >= 1 && n <= NUMBERED.length) {
    event.preventDefault()
    NUMBERED[n - 1]()
  }
}

watch(open, async (now) => {
  if (now) {
    const { from, to } = chartRange.value
    draft.value = { from: from ?? '', to: to ?? '' }
    document.addEventListener('keydown', onKey)
    await nextTick()
    panel.value?.focus({ preventScroll: true })
  } else {
    document.removeEventListener('keydown', onKey)
    // Back to the button, if the focus was in here (a click elsewhere keeps it where it went).
    if (!document.activeElement || document.activeElement === document.body || panel.value?.contains(document.activeElement)) {
      trigger.value?.focus({ preventScroll: true })
    }
  }
})
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKey)
  state.ui.rangeOpen = false
})
</script>

<template>
  <div class="relative">
    <!-- Text and an icon rather than a button-shaped thing: it sits among the
         header's other controls and should weigh what they weigh. The tone goes
         on the children — `button { color: inherit }` outranks any utility put
         on the button itself. -->
    <button
      ref="trigger"
      class="inline-flex items-center gap-1.5 rounded-lg px-1.5 py-1 text-[13px] font-medium
             transition hover:bg-raised"
      :aria-expanded="open"
      :aria-label="t('range.change', { label })"
      :aria-keyshortcuts="ariaCombo(keysOf('range'))"
      data-range-trigger
      @click.stop="toggle"
    >
      <Icon name="calendar" :size="16" :class="chartRange.set ? 'text-accent' : 'text-muted'" />
      <span
        class="max-w-[9.5rem] truncate"
        :class="chartRange.set ? 'text-accent' : 'text-muted'"
      >
        {{ label }}
      </span>
      <Icon name="down" :size="12" class="text-faint transition" :class="open && 'rotate-180'" />
      <Kbd :combo="keysOf('range')" />
    </button>

    <template v-if="open">
      <!-- Anywhere else is "never mind". -->
      <div class="fixed inset-0 z-30" @click="open = false" />
      <div
        ref="panel"
        class="absolute right-0 z-40 mt-1.5 w-[21.5rem] max-w-[calc(100vw-1.5rem)] rounded-xl
               border border-line bg-surface p-3 shadow-[var(--c-shadow)] focus:outline-none"
        role="dialog"
        tabindex="-1"
        :aria-label="t('range.title')"
        data-range
      >
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="label flex items-center gap-1" for="range-from">{{ t('range.fromLabel') }} <Kbd combo="1" /></label>
            <input id="range-from" ref="fromEl" v-model="draft.from" type="date" class="input !py-1.5 !text-[13px]" />
          </div>
          <div>
            <label class="label flex items-center gap-1" for="range-to">{{ t('range.toLabel') }} <Kbd combo="2" /></label>
            <input id="range-to" ref="toEl" v-model="draft.to" type="date" class="input !py-1.5 !text-[13px]" />
          </div>
        </div>
        <div class="mt-2 flex flex-wrap gap-1.5">
          <button
            v-for="preset in PRESETS"
            :key="preset.key"
            class="chip !gap-1 !py-1 !text-[12px]"
            :aria-keyshortcuts="numberOf(preset)"
            :data-preset="preset.key"
            @click="usePreset(preset)"
          >
            {{ t(`range.preset.${preset.key}`) }}
            <Kbd :combo="numberOf(preset)" />
          </button>
        </div>
        <div class="mt-3 flex items-center gap-2 whitespace-nowrap">
          <button class="btn-ghost !gap-1 !px-2 !py-1.5 !text-[12.5px]" :aria-keyshortcuts="ALL_TIME" @click="clear">
            {{ t('range.allTime') }} <Kbd :combo="ALL_TIME" />
          </button>
          <div class="flex-1" />
          <button class="btn-soft !gap-1 !px-3 !py-1.5 !text-[12.5px]" :aria-keyshortcuts="ariaCombo('escape')" @click="open = false">
            {{ t('common.cancel') }} <Kbd combo="escape" />
          </button>
          <button class="btn-primary !gap-1 !px-3 !py-1.5 !text-[12.5px]" :aria-keyshortcuts="ariaCombo('mod+enter')" @click="apply">
            {{ t('common.apply') }} <Kbd combo="mod+enter" />
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
